import { useEffect, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { $creators, $isCreatorProcessCompleted } from '@/models/creators.ts'
import type { Creator } from '@/modules/creators/types.ts'
import { billingService } from '@/modules/licenses/service/billing-service.ts'
import { licensesService } from '@/modules/licenses/service/licenses-service.ts'
import { handleError } from '@/utils/error'

import type { BillingPlan, License, LicensesTableType } from '../types'

export function useLicensesTable() {
  const creators = useUnit($creators)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const [licenseQuantity, setLicenseQuantity] = useState<number>(0)
  const [licensesUsed, setLicensesUsed] = useState<number>(0)
  const [licensesTable, setLicensesTable] = useState<LicensesTableType[]>([])

  const { data, isFetching: isLicensesLoading } = useQuery(
    'licenses',
    async () => {
      return await licensesService.getLicenses()
    },
    {
      onError: (error) => handleError(error, 'Failed to get licenses'),
    },
  )

  const { data: plans, isFetching: isPlansLoading } = useQuery(
    ['billing'],
    async () => {
      return await billingService.getPlans()
    },
    {
      onError: (error) => handleError(error, 'Failed to get plans'),
    },
  )

  const { data: manageLink } = useQuery(
    ['billing-portal'],
    async () => {
      return await billingService.getPortalLink()
    },
    {
      onError: (error) =>
        handleError(error, 'Failed to get billing portal link'),
    },
  )

  useEffect(() => {
    if (isLicensesLoading || !data || data.length === 0) return

    const quantity = data.reduce((sum, curr) => sum + curr.quantity, 0)

    setLicenseQuantity(quantity)

    const used = data.reduce((sum, curr) => sum + curr.activeUsed, 0)

    setLicensesUsed(used)
  }, [isLicensesLoading, data])

  useEffect(() => {
    if (isLicensesLoading || !data || !plans || !isCreatorProcessCompleted)
      return

    setLicensesTable(mapLicensesTable(creators, data, plans))
  }, [isLicensesLoading, data, isCreatorProcessCompleted, creators, plans])

  return useMemo(
    () => ({
      manageLink,
      licenseQuantity,
      licensesUsed,
      licenses: data,
      plans,
      licensesTable,
      isFetching:
        isLicensesLoading || isPlansLoading || !isCreatorProcessCompleted,
    }),
    [
      manageLink,
      licenseQuantity,
      licensesUsed,
      data,
      plans,
      licensesTable,
      isLicensesLoading,
      isPlansLoading,
      isCreatorProcessCompleted,
    ],
  )
}

function mapLicensesTable(
  creators: Creator[],
  licenses: License[],
  plans: BillingPlan[],
) {
  const data: LicensesTableType[] = []

  for (const license of licenses) {
    const planName = license.plan.substring(0, license.plan.indexOf('_'))
    const planVariantName = license.plan.includes('_')
      ? license.plan.substring(license.plan.indexOf('_') + 1)
      : ''
    const isYearlyLicense = planVariantName === 'yearly'
    const purchasePlan = plans.find((p) => p.name === planName)
    const mapLicense = (creator: Creator | undefined) => {
      return {
        licenseId: license.id,
        planLevel: purchasePlan?.level ?? 0,
        planDisplayName: purchasePlan?.displayName || license.plan,
        planName: planName,
        isCreatorConnected: creator != undefined,
        isYearly: isYearlyLicense,
        planVariantName:
          planVariantName.substring(0, 1).toUpperCase() +
          planVariantName.substring(1).toLowerCase(),
        creator: creator,
        disabled: license.disabled,
      }
    }

    for (const creator of creators.filter((c) => c.licenseId === license.id)) {
      data.push(mapLicense(creator))
    }

    for (
      let index = 0;
      index < license.quantity - license.activeUsed;
      index++
    ) {
      data.push(mapLicense(undefined))
    }
  }

  return data.sort(
    (a, b) =>
      a.planDisplayName.length - b.planDisplayName.length ||
      (a.isYearly === b.isYearly ? 0 : !a.isYearly ? -1 : 1) ||
      (a.isCreatorConnected ? -1 : 1),
  )
}
